import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Transition } from "../../pages/app/utils/animation";
import { useModals } from "../../store/dialogs/hooks";
import { Controller, useForm } from "react-hook-form";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { NLMS_OUTPUT_MODE_TAP_NUMBER } from "../../pages/app/utils/constants";

const NLMSSchema = (sampleRate) => {
  const maxNumTaps = sampleRate / 2;

  return z.object({
    stepSize: z.preprocess(
      (val) => (val === "" ? undefined : val),
      z.coerce
        .number({
          message: "Please enter a valid number.",
        })
        .min(0.0001, "Must be between 0.0001 and 0.1.")
        .max(0.1, "Must be between 0.0001 and 0.1.")
        .refine((val) => Number(val.toFixed(4)) === val, {
          message: "Maximum 4 decimal places allowed.",
        }),
    ),
    numTaps: z
      .union([
        z.string(),
        z.number(),
        z.object({
          id: z.number().optional(),
          label: z.string(),
          value: z.number(),
        }),
      ])
      .nullable()
      .superRefine((val, ctx) => {
        if (val === null || val === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please select or enter a valid number.",
          });
          return;
        }

        const rawVal = val?.value ?? val;
        const num = Number(rawVal);

        if (isNaN(num) || String(rawVal).trim() === "") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please enter a valid number.",
          });
          return;
        }
        if (num < 5) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Value must be at least 5.",
          });
        }
        if (num >= maxNumTaps) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Value must be less than ${maxNumTaps}.`,
          });
        }
      }),
    sampleDelay: z.preprocess(
      (val) => (val === "" ? undefined : val),
      z.coerce
        .number({
          message: "Please enter a valid number.",
        })
        .min(1, "Must be at least 1.")
        .max(1000, "Must be less than 1000."),
    ),
  });
};

export const NLMSFilterDialog = ({ onRun, sampleRate }) => {
  const { closeNLMSFilterDialog, showNLMSFilterDialog } = useModals();

  const {
    control,
    handleSubmit,
    reset: rhfReset,
    formState: { errors, isDirty, isValid },
    trigger,
  } = useForm({
    resolver: zodResolver(NLMSSchema(sampleRate)),
    values: {
      stepSize: 0.01,
      numTaps: NLMS_OUTPUT_MODE_TAP_NUMBER[5],
      sampleDelay: 100,
    },
    mode: "all",
  });

  const handleClose = () => {
    rhfReset();
    closeNLMSFilterDialog();
  };

  const onSubmit = (data) => {
    onRun({
      ...data,
      numTaps: Number(data.numTaps?.value ?? data.numTaps),
    });
    rhfReset();
    handleClose();
  };

  return (
    <Dialog
      aria-hidden={!showNLMSFilterDialog}
      open={showNLMSFilterDialog}
      onClose={handleClose}
      closeAfterTransition={false}
      slots={{
        transition: Transition,
      }}
    >
      <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>NLMS</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 4 }}>
            Please edit the settings below as needed to run the NLMS filter. If
            you are unsure of the settings, try using the default values.
          </DialogContentText>
          <Grid container columnSpacing={2} rowSpacing={4}>
            <Grid size={{ xs: 4 }}>
              <Controller
                name="stepSize"
                control={control}
                defaultValue={0.0001}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Step Size"
                    type="number"
                    fullWidth
                    helperText={
                      errors.stepSize
                        ? errors.stepSize?.message
                        : "The step size should be between 0.0001 and 0.1."
                    }
                    error={!!errors.stepSize}
                    slotProps={{
                      htmlInput: { min: 0.0001, max: 0.1, step: 0.0001 },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Controller
                name="numTaps"
                control={control}
                render={({ field: { onChange, value, onBlur, ref } }) => (
                  <Autocomplete
                    freeSolo
                    options={NLMS_OUTPUT_MODE_TAP_NUMBER.filter(
                      (option) => option.value < sampleRate / 2,
                    )}
                    value={value}
                    getOptionLabel={(option) => {
                      if (typeof option === "object")
                        return option?.label ?? "";
                      return String(option ?? "");
                    }}
                    onChange={(_, newValue) => {
                      onChange(newValue);
                      trigger("numTaps");
                    }}
                    onInputChange={(_, newInputValue) => {
                      if (!newInputValue.trim()) {
                        onChange(null);
                        return;
                      }
                      if (
                        typeof value === "object" &&
                        value?.label === newInputValue
                      ) {
                        return;
                      }
                      onChange(newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputRef={ref}
                        onBlur={onBlur}
                        label="Filter Length (samples)"
                        variant="outlined"
                        error={!!errors.numTaps}
                        helperText={
                          errors.numTaps
                            ? errors.numTaps.message
                            : "The number of audio samples in the filter."
                        }
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Controller
                name="sampleDelay"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="number"
                    label="Sample Delay"
                    helperText={
                      errors.sampleDelay
                        ? errors.sampleDelay.message
                        : "The sample delay in milliseconds."
                    }
                    error={!!errors.sampleDelay}
                    slotProps={{
                      htmlInput: { min: 0, max: 1000, step: 10 },
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">ms</InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button
            disabled={!isDirty}
            onClick={() => rhfReset()}
            variant="outlined"
          >
            Reset to Default
          </Button>
          <Button disabled={!isValid} type="submit" variant="contained">
            Run
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
